import React, { useState } from 'react';
import './forteen-design-system.css';
import { Button, Badge, ProgressTracker, AudioToggle } from './forteen-components';
import { scenario1 } from './scenarios';
import { scoreResponse } from './feedbackEngine';

function App() {
  // App state
  const [screen, setScreen] = useState('landing'); // landing | scenario | feedback
  const [userResponse, setUserResponse] = useState('');
  const [scores, setScores] = useState(null);
  const [audioMode, setAudioMode] = useState(false);
  const [viewingAlternative, setViewingAlternative] = useState(null);

  // Handle response submission
  const handleSubmit = () => {
    if (!userResponse.trim()) return;
    
    const feedback = scoreResponse(userResponse, scenario1.rubric);
    setScores(feedback);
    setScreen('feedback');
    
    // Track analytics (localStorage for now)
    const analytics = JSON.parse(localStorage.getItem('voiceSprintAnalytics') || '[]');
    analytics.push({
      scenarioId: 1,
      timestamp: Date.now(),
      scores: feedback,
      responseLength: userResponse.length
    });
    localStorage.setItem('voiceSprintAnalytics', JSON.stringify(analytics));
  };

  // Reset to try again
  const handleTryAgain = () => {
    setUserResponse('');
    setScores(null);
    setScreen('scenario');
    setViewingAlternative(null);
  };

  // Landing Screen
  if (screen === 'landing') {
    return (
      <div className="container safe-area-top safe-area-bottom">
        <div className="section">
          <div style={{
            background: 'linear-gradient(135deg, #5B5F97 0%, #7B7FB7 100%)',
            color: 'white',
            padding: '40px 24px',
            borderRadius: '16px',
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            <h1 className="text-4xl font-bold mb-md">💬 Voice Sprint</h1>
            <p className="text-xl mb-sm">Master Managing Upward</p>
            <p className="text-base" style={{ opacity: 0.9 }}>
              Practice workplace communication in 90-second scenarios
            </p>
          </div>

          <div className="card mb-lg">
            <div className="card-header">
              <div className="card-icon card-icon-voice">💬</div>
              <div>
                <h2 className="card-title">Scenario 1: Asking for Clarity</h2>
                <Badge level="surviving">Surviving</Badge>
              </div>
            </div>
            
            <div className="card-body">
              <p className="mb-md">
                <strong>What You'll Practice:</strong>
              </p>
              <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
                <li>How to ask specific questions without seeming incompetent</li>
                <li>Turning vague instructions into clear action items</li>
                <li>Professional tone without over-apologizing</li>
              </ul>
              
              <div style={{
                background: '#E3F2FD',
                padding: '16px',
                borderRadius: '8px',
                marginTop: '16px',
                borderLeft: '4px solid #42A5F5'
              }}>
                <strong style={{ color: '#1976D2' }}>⏱️ Duration:</strong> 90 seconds
                <br />
                <strong style={{ color: '#1976D2' }}>📊 Feedback:</strong> 4 dimensions (Clarity, Respect, Solution-Focus, Assertiveness)
              </div>
            </div>
            
            <div className="card-footer" style={{ borderTop: 'none', paddingTop: 0 }}>
              <Button 
                variant="secondary" 
                fullWidth 
                onClick={() => setScreen('scenario')}
              >
                Start Scenario
              </Button>
            </div>
          </div>

          <div style={{
            background: '#FFF8E1',
            padding: '20px',
            borderRadius: '12px',
            borderLeft: '4px solid #FFC107'
          }}>
            <strong>💡 How It Works:</strong>
            <ol style={{ marginLeft: '20px', marginTop: '12px', lineHeight: '1.8' }}>
              <li>Read the workplace scenario</li>
              <li>Type how you'd respond</li>
              <li>Get instant feedback on 4 dimensions</li>
              <li>See alternative approaches</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Scenario Screen
  if (screen === 'scenario') {
    return (
      <div className="container safe-area-top safe-area-bottom">
        <div className="flex-between mb-lg">
          <h2 className="text-2xl font-bold text-purple">Scenario 1</h2>
          <AudioToggle 
            isAudioMode={audioMode}
            onToggle={() => setAudioMode(!audioMode)}
          />
        </div>

        {/* Scenario Content */}
        <div className="card mb-lg">
          <div className="card-header">
            <div className="card-icon card-icon-voice">💬</div>
            <div>
              <h3 className="card-title">{scenario1.title}</h3>
              <Badge level="surviving">Surviving</Badge>
            </div>
          </div>
          
          <div className="card-body">
            {audioMode ? (
              <div style={{
                background: '#F3E5F5',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎧</div>
                <p className="text-secondary mb-md">Audio narration coming soon!</p>
                <p className="text-sm text-secondary">For now, please use reading mode.</p>
                <Button 
                  variant="outline" 
                  onClick={() => setAudioMode(false)}
                  style={{ marginTop: '16px' }}
                >
                  Switch to Reading Mode
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-md">
                  <strong style={{ color: '#5B5F97', fontSize: '1.1rem' }}>🎯 The Situation</strong>
                  <p style={{ marginTop: '12px', lineHeight: '1.6' }}>
                    {scenario1.setup.text}
                  </p>
                </div>

                <div style={{
                  background: '#FFF8E1',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <strong style={{ color: '#F57C00' }}>⚠️ The Challenge</strong>
                  <p style={{ marginTop: '8px', lineHeight: '1.6' }}>
                    {scenario1.challenge}
                  </p>
                </div>

                <div style={{
                  background: '#E8F5E9',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <strong style={{ color: '#2E7D32' }}>💚 Why This Is Hard</strong>
                  <p style={{ marginTop: '8px', lineHeight: '1.6' }}>
                    {scenario1.validation}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Optional: View Alternatives First */}
        {!audioMode && (
          <div className="card mb-lg">
            <h3 className="text-xl font-semibold mb-md">🤔 Want to see examples first?</h3>
            <p className="text-secondary mb-md">
              You can view 3 different approaches before writing your own response.
            </p>
            <Button 
              variant="outline" 
              fullWidth
              onClick={() => setViewingAlternative(0)}
            >
              Show Me Examples
            </Button>
          </div>
        )}

        {/* Response Input */}
        {!audioMode && !viewingAlternative && (
          <div className="card mb-lg">
            <h3 className="text-xl font-semibold mb-md">✍️ Your Response</h3>
            <p className="text-secondary mb-md">
              How would you respond to Priya? Type your email/message below.
            </p>
            
            <textarea 
              className="input-textarea"
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '16px',
                fontSize: '16px',
                borderRadius: '8px',
                border: '2px solid #E0E0E0',
                fontFamily: 'inherit',
                lineHeight: '1.6',
                marginBottom: '16px'
              }}
              placeholder="Hi Priya,

Quick clarification to make sure I deliver what you need..."
              value={userResponse}
              onChange={(e) => setUserResponse(e.target.value)}
            />
            
            <div className="flex-between" style={{ gap: '12px' }}>
              <Button 
                variant="outline"
                onClick={() => setScreen('landing')}
              >
                ← Back
              </Button>
              <Button 
                variant="secondary"
                onClick={handleSubmit}
                disabled={!userResponse.trim()}
              >
                Submit Response →
              </Button>
            </div>
          </div>
        )}

        {/* Alternative Responses View */}
        {viewingAlternative !== null && (
          <AlternativesView 
            alternatives={scenario1.alternatives}
            currentIndex={viewingAlternative}
            onNext={() => setViewingAlternative(prev => 
              prev < scenario1.alternatives.length - 1 ? prev + 1 : prev
            )}
            onPrev={() => setViewingAlternative(prev => prev > 0 ? prev - 1 : prev)}
            onClose={() => setViewingAlternative(null)}
          />
        )}
      </div>
    );
  }

  // Feedback Screen
  if (screen === 'feedback') {
    const overall = (scores.clarity + scores.respect + scores.solutionFocus + scores.assertiveness) / 4;
    const zone = overall >= 4.0 ? 'assertive' : overall >= 3.0 ? 'good' : overall >= 2.0 ? 'needs-work' : 'try-again';
    
    return (
      <div className="container safe-area-top safe-area-bottom">
        <div className="section">
          <h2 className="text-3xl font-bold mb-lg text-center">📊 Your Feedback</h2>

          {/* Overall Score */}
          <div className="card mb-lg" style={{
            background: zone === 'assertive' ? 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)' :
                         zone === 'good' ? 'linear-gradient(135deg, #5B5F97 0%, #7B7FB7 100%)' :
                         zone === 'needs-work' ? 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)' :
                         'linear-gradient(135deg, #F44336 0%, #EF5350 100%)',
            color: 'white',
            textAlign: 'center',
            padding: '32px'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>
              {zone === 'assertive' ? '🎯' : zone === 'good' ? '👍' : zone === 'needs-work' ? '⚠️' : '🔄'}
            </div>
            <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '8px' }}>
              {overall.toFixed(1)}/5.0
            </div>
            <div style={{ fontSize: '1.3rem', opacity: 0.95 }}>
              {zone === 'assertive' ? 'ASSERTIVE ZONE ✅' :
               zone === 'good' ? 'GOOD EFFORT 👍' :
               zone === 'needs-work' ? 'NEEDS WORK ⚠️' :
               'TRY AGAIN 🔄'}
            </div>
          </div>

          {/* Dimension Scores */}
          <div className="card mb-lg">
            <h3 className="text-xl font-semibold mb-md">Your Scores</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <div className="flex-between mb-sm">
                <span className="font-semibold">Clarity</span>
                <span className="text-orange font-bold">{scores.clarity}/5</span>
              </div>
              <ProgressTracker current={scores.clarity} total={5} gradient="primary" />
              <p className="text-sm text-secondary mt-xs">{scores.clarityFeedback}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div className="flex-between mb-sm">
                <span className="font-semibold">Respect</span>
                <span className="text-purple font-bold">{scores.respect}/5</span>
              </div>
              <ProgressTracker current={scores.respect} total={5} gradient="secondary" />
              <p className="text-sm text-secondary mt-xs">{scores.respectFeedback}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div className="flex-between mb-sm">
                <span className="font-semibold">Solution-Focus</span>
                <span className="font-bold" style={{ color: '#42A5F5' }}>{scores.solutionFocus}/5</span>
              </div>
              <ProgressTracker current={scores.solutionFocus} total={5} gradient="primary" />
              <p className="text-sm text-secondary mt-xs">{scores.solutionFocusFeedback}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div className="flex-between mb-sm">
                <span className="font-semibold">Assertiveness</span>
                <span className="text-success font-bold">{scores.assertiveness}/5</span>
              </div>
              <ProgressTracker current={scores.assertiveness} total={5} gradient="success" />
              <p className="text-sm text-secondary mt-xs">{scores.assertivenessFeedback}</p>
            </div>
          </div>

          {/* What Worked / To Improve */}
          <div className="card mb-lg">
            <h4 className="font-semibold mb-sm" style={{ color: '#4CAF50' }}>✅ What Worked Well:</h4>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', marginBottom: '16px' }}>
              {scores.strengths.map((strength, i) => (
                <li key={i}>{strength}</li>
              ))}
            </ul>
            
            {scores.improvements.length > 0 && (
              <>
                <h4 className="font-semibold mb-sm" style={{ color: '#FF9800' }}>💡 To Improve:</h4>
                <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
                  {scores.improvements.map((improvement, i) => (
                    <li key={i}>{improvement}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* View Alternatives */}
          <div className="card mb-lg">
            <h3 className="text-xl font-semibold mb-md">🎯 See How Others Would Respond</h3>
            <p className="text-secondary mb-md">
              Compare your approach to 3 different response styles.
            </p>
            <Button 
              variant="outline"
              fullWidth
              onClick={() => {
                setViewingAlternative(0);
                setScreen('scenario');
              }}
            >
              View Alternative Responses
            </Button>
          </div>

          {/* Actions */}
          <div className="flex-col gap-md">
            <Button 
              variant="secondary"
              fullWidth
              onClick={handleTryAgain}
            >
              🔄 Try Again
            </Button>
            <Button 
              variant="outline"
              fullWidth
              onClick={() => setScreen('landing')}
            >
              ← Back to Home
            </Button>
          </div>

          {/* Acknowledge Limits */}
          <div style={{
            background: '#FFF8E1',
            padding: '20px',
            borderRadius: '12px',
            marginTop: '24px',
            borderLeft: '4px solid #FFC107'
          }}>
            <strong>💭 Real Talk:</strong>
            <p style={{ marginTop: '8px', lineHeight: '1.6' }}>
              If your manager is truly unreasonable or chronically unclear, 
              even perfect communication won't fix it. That's okay—you'll know 
              you tried the professional approach. Sometimes the environment 
              is the problem, not you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// Alternative Responses Component
function AlternativesView({ alternatives, currentIndex, onNext, onPrev, onClose }) {
  const alt = alternatives[currentIndex];
  const overall = (alt.score.clarity + alt.score.respect + alt.score.solutionFocus + alt.score.assertiveness) / 4;
  
  return (
    <div className="card mb-lg">
      <div className="flex-between mb-md">
        <h3 className="text-xl font-semibold">
          Example {currentIndex + 1} of {alternatives.length}
        </h3>
        <Button variant="ghost" onClick={onClose}>✕</Button>
      </div>

      <div style={{
        background: alt.type === 'assertive' ? '#E8F5E9' :
                   alt.type === 'aggressive' ? '#FFEBEE' :
                   '#FFF8E1',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '16px',
        borderLeft: `4px solid ${
          alt.type === 'assertive' ? '#4CAF50' :
          alt.type === 'aggressive' ? '#F44336' :
          '#FF9800'
        }`
      }}>
        <strong style={{
          color: alt.type === 'assertive' ? '#2E7D32' :
                 alt.type === 'aggressive' ? '#C62828' :
                 '#F57C00',
          fontSize: '1.1rem'
        }}>
          {alt.title}
        </strong>
      </div>

      <div style={{
        background: '#F8F9FA',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '16px',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.6'
      }}>
        {alt.response}
      </div>

      {/* Scores */}
      <div style={{
        background: '#FAFAFA',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <div className="flex-between mb-sm">
          <span className="text-sm font-semibold">Overall Score:</span>
          <span className="font-bold" style={{ 
            color: overall >= 4 ? '#4CAF50' : overall >= 3 ? '#5B5F97' : '#FF9800',
            fontSize: '1.2rem'
          }}>
            {overall.toFixed(1)}/5.0
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.85rem' }}>
          <div>Clarity: {alt.score.clarity}/5</div>
          <div>Respect: {alt.score.respect}/5</div>
          <div>Solution-Focus: {alt.score.solutionFocus}/5</div>
          <div>Assertiveness: {alt.score.assertiveness}/5</div>
        </div>
      </div>

      {/* Feedback */}
      <div style={{
        background: '#E3F2FD',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '16px',
        borderLeft: '4px solid #42A5F5'
      }}>
        <strong style={{ color: '#1976D2' }}>📝 Analysis:</strong>
        <p style={{ marginTop: '8px', lineHeight: '1.6' }}>{alt.feedback}</p>
      </div>

      {/* Navigation */}
      <div className="flex-between">
        <Button 
          variant="outline"
          onClick={onPrev}
          disabled={currentIndex === 0}
        >
          ← Previous
        </Button>
        <Button 
          variant="outline"
          onClick={onNext}
          disabled={currentIndex === alternatives.length - 1}
        >
          Next →
        </Button>
      </div>

      <Button 
        variant="secondary"
        fullWidth
        onClick={onClose}
        style={{ marginTop: '16px' }}
      >
        Write My Own Response
      </Button>
    </div>
  );
}

export default App;
